import React, { useEffect, useRef, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { onValue, ref } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { database, firestore, functions } from '../firebaseConfig';
import { qualityChecksPerMonth, resolveMembershipPlanId } from '../config/creditPlans';
import './OutreachQualityCheck.css';

const LANE_LABELS = {
  identity: 'Name & footer',
  recipient: 'Greeting',
  server: 'Rules',
  audience: 'Audience',
  detail: 'Writing',
  spam: 'Inbox',
  opus: 'Campaign',
  sonnet: 'Repetition',
  trust: 'Trust',
  advise: 'Suggestions',
};

const harmStep = (value) => {
  if (value === 'kritisch' || value === 'sofort') return 4;
  if (value === 'hoch' || value === 'diese_woche') return 3;
  if (value === 'niedrig' || value === 'spaeter') return 1;
  return 2;
};

const PROGRESS_STEPS = [
  { id: 'sample', title: 'Pick emails', text: 'From the latest 200 sent: up to 30 intros and 10 per follow-up step.' },
  { id: 'identity', title: 'Name, greeting, and footer', text: 'Sender, sign-off, greeting name, and footer checked against each other.' },
  { id: 'read', title: 'Read the campaign', text: 'What is offered, the voice they want, and who the recipients are.' },
  { id: 'judge', title: 'Read like a writer', text: 'Reads the copy like a writer, compares the emails, and checks where trust actually breaks.' },
  { id: 'advise', title: 'Suggestions', text: 'The changes, most urgent first.' },
  { id: 'save', title: 'Save the report', text: 'Filed with the other analytics reports.' },
];

const PROGRESS_DETAIL = {
  'Zieht die Mails der letzten 30 Tage.': 'Pulling emails from the last 30 days.',
  'Versteht Angebot, Stimme und Empfänger, prüft jede Mail gegen ihren Empfänger.': 'Reading the offer, the voice, and the recipients, then checking each email against its recipient.',
  'Liest die Texte wie ein Writer und prüft, wo Vertrauen wirklich kippt.': 'Reading the copy like a writer and checking where trust actually breaks.',
  'Schreibt die Änderungen, die dringendste zuerst.': 'Writing the changes, most urgent first.',
  'Speichert den Report bei den Analytics.': 'Saving the report with the other analytics.',
};

const progressDetail = (detail, fallback) => {
  const text = String(detail || '').trim();
  if (!text) return fallback;
  const named = text.match(/^Prüft Name und Footer in (\d+) Mails\.$/);
  if (named) return `Checking name and footer across ${named[1]} emails.`;
  if (PROGRESS_DETAIL[text]) return PROGRESS_DETAIL[text];
  if (/[äöüÄÖÜß]/.test(text) || /\b(Mails|Prüft|Postfach)\b/.test(text)) return fallback;
  return text;
};

const PLACEMENT_LABELS = {
  inbox: 'Inbox',
  promotions: 'Promotions',
  spam: 'Spam',
};

const SCORE_DIMENSIONS = [
  { id: 'absender', title: 'Sender', lanes: ['identity'] },
  { id: 'postfach', title: 'Inbox', lanes: ['server', 'spam'] },
  { id: 'text', title: 'Writing', lanes: ['detail', 'sonnet', 'opus', 'audience'] },
  { id: 'vertrauen', title: 'Trust', lanes: ['trust'] },
];

const penaltyOf = (findings) => {
  const weight = { kritisch: 22, hoch: 12, mittel: 5, niedrig: 2 };
  return (findings || []).reduce((sum, item) => {
    const base = weight[item && item.severity] || 5;
    const times = Math.min(Math.max(Number(item && item.count) || 1, 1), 12);
    return sum + base * (1 + Math.log2(times) * 0.35);
  }, 0);
};

const curveScore = (raw) => Math.max(0, Math.min(100, Math.round(100 * Math.exp(-(Number(raw) || 0) / 80))));

const dimensionIdOf = (item) => {
  const lanes = (item && item.lanes && item.lanes.length ? item.lanes : [item && item.lane]).filter(Boolean).map(String);
  const hit = SCORE_DIMENSIONS.find((dim) => dim.lanes.some((lane) => lanes.includes(lane) || lanes.some((name) => name.startsWith(lane))));
  return hit ? hit.id : 'text';
};

const scoreReport = (findings, serverScore) => {
  const list = Array.isArray(findings) ? findings : [];
  const fromFindings = curveScore(penaltyOf(list));
  const server = Number(serverScore);
  const score = Number.isFinite(server)
    ? Math.max(0, Math.min(100, Math.round(fromFindings * 0.8 + server * 0.2)))
    : fromFindings;
  const dimensions = SCORE_DIMENSIONS.map((dim) => {
    const mine = list.filter((item) => dimensionIdOf(item) === dim.id);
    let value = curveScore(penaltyOf(mine));
    if (dim.id === 'postfach' && Number.isFinite(server)) value = Math.round(value * 0.5 + server * 0.5);
    return { id: dim.id, title: dim.title, score: value, note: '' };
  });
  return { score, dimensions };
};

const scoreTone = (value) => (value >= 75 ? 'good' : value >= 50 ? 'mid' : 'low');

const DIMENSION_TITLES = {
  de: { absender: 'Absender', postfach: 'Postfach', text: 'Text', vertrauen: 'Vertrauen' },
  en: { absender: 'Sender', postfach: 'Inbox', text: 'Writing', vertrauen: 'Trust' },
};

const PLACEMENT_BY_LANG = {
  de: { inbox: 'Posteingang', promotions: 'Werbung', spam: 'Spam' },
  en: { inbox: 'Inbox', promotions: 'Promotions', spam: 'Spam' },
};

const dimensionTitle = (dim, lang) => {
  const titles = DIMENSION_TITLES[lang] || DIMENSION_TITLES.de;
  if (dim && titles[dim.id]) return titles[dim.id];
  return String((dim && dim.title) || '');
};

const accountLine = (label, lang) => {
  const text = String(label || '');
  if (lang === 'de') return text.replace(/^All accounts/, 'Alle Postfächer');
  return text.replace(/^Alle Postfächer/, 'All accounts');
};

const shortDate = (iso, lang) => {
  const ms = Date.parse(iso || '');
  return ms ? new Date(ms).toLocaleDateString(lang === 'en' ? 'en-US' : 'de-DE', { day: 'numeric', month: 'short' }) : '';
};

const compositionText = (report, lang) => {
  const rows = Array.isArray(report && report.composition) ? report.composition : [];
  if (!rows.length) return '';
  const parts = rows.map((row) => `${row.count} ${row.label}`).join(', ');
  const from = shortDate(report.periodStart, lang);
  const to = shortDate(report.periodEnd, lang);
  const span = from && to ? (from === to ? ` · ${from}` : ` · ${from} – ${to}`) : '';
  return lang === 'de'
    ? `Geprüft: ${parts}, aus den letzten ${report.poolSize || 200} gesendeten${span}`
    : `Checked: ${parts}, from the latest ${report.poolSize || 200} sent${span}`;
};

const cardTitle = (item) => {
  const title = String((item && item.title) || '').trim();
  if (title) return title;
  return String((item && item.problem) || '').split(/(?<=[.!?])\s/)[0].trim();
};

const firstSentence = (text) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  const end = clean.search(/[.!?](\s|$)/);
  const sentence = end === -1 ? clean : clean.slice(0, end + 1);
  return sentence.length > 170 ? `${sentence.slice(0, 168)}…` : sentence;
};

const shareLabel = (count, sampleSize, lang) => {
  const times = Number(count) || 0;
  const sample = Number(sampleSize) || 0;
  if (times > 1 && sample > 0) {
    const shown = Math.min(times, sample);
    return lang === 'de' ? `${shown} von ${sample}` : `${shown} of ${sample}`;
  }
  if (times > 1) return lang === 'de' ? `${times} Mal` : `${times} times`;
  return '';
};

const meterLine = (dim, findings, sampleSize, lang) => {
  if (dim && dim.note) return dim.note;
  const top = (findings || []).find((item) => dimensionIdOf(item) === dim.id);
  if (!top) return lang === 'de' ? 'Hier ist nichts hängen geblieben.' : 'Nothing stood out here.';
  const share = shareLabel(top.count, sampleSize, lang);
  return [cardTitle(top), share].filter(Boolean).join(', ');
};

function PointNo({ n }) {
  return <span className="oqc-idx">{n}</span>;
}

function HarmScale({ value }) {
  const step = harmStep(value);
  return (
    <span className={`oqc-scale oqc-scale--${step}`} title={`${step} of 4`} aria-label={`Severity ${step} of 4`}>
      <i /><i /><i /><i />
    </span>
  );
}

const REACH_TEXT = {
  de: {
    once: 'Einzelfall',
    always: 'In jeder Mail',
    repeat: 'Mehrfach',
    templates: 'Mehrere Templates',
  },
  en: {
    once: 'One-off',
    always: 'Every email',
    repeat: 'Repeats',
    templates: 'Several templates',
  },
};

function HitStrip({ hits, total }) {
  const size = Math.min(Math.max(Number(total) || 0, Number(hits) || 0), 12);
  if (size < 2) return null;
  const filled = Math.min(Number(hits) || 0, size);
  return (
    <span className="oqc-hits" aria-hidden="true">
      {Array.from({ length: size }, (_, index) => <i key={index} className={index < filled ? 'is-hit' : ''} />)}
    </span>
  );
}

const groupName = (group, lang) => (group.templateId
  ? (lang === 'de' ? `dem Template „${group.name}“` : `the template “${group.name}”`)
  : group.name);

const whereSentence = (where, lang) => {
  const top = where.where[0];
  const from = groupName(top, lang);
  if (where.reach === 'once') {
    if (top.total > 1) {
      return lang === 'de'
        ? `1 von ${top.total} geprüften Mails aus ${from}`
        : `1 of ${top.total} checked emails from ${from}`;
    }
    return lang === 'de' ? `Die einzige geprüfte Mail aus ${from}` : `The only checked email from ${from}`;
  }
  if (where.reach === 'always') {
    return lang === 'de'
      ? `Alle ${top.total} geprüften Mails aus ${from}`
      : `All ${top.total} checked emails from ${from}`;
  }
  if (where.reach === 'repeat') {
    return lang === 'de'
      ? `${top.hits} von ${top.total} geprüften Mails aus ${from}`
      : `${top.hits} of ${top.total} checked emails from ${from}`;
  }
  return lang === 'de'
    ? `${where.mails} Mails aus ${where.where.length} Templates`
    : `${where.mails} emails from ${where.where.length} templates`;
};

function WhereLine({ where, lang }) {
  if (!where || !Array.isArray(where.where) || !where.where.length) return null;
  const words = REACH_TEXT[lang] || REACH_TEXT.de;
  const top = where.where[0];
  const variant = top.variants && top.variants[0];
  const variantBound = variant && top.variants.length === 1 && variant.hits === top.hits && top.hits > 1;
  return (
    <div className={`oqc-where oqc-where--${where.reach}`}>
      <p className="oqc-where-line">
        <b>{words[where.reach] || words.repeat}</b>
        <span>{whereSentence(where, lang)}</span>
        {where.reach !== 'templates' && <HitStrip hits={top.hits} total={top.total} />}
      </p>
      {variant && where.reach !== 'templates' && (
        <p className="oqc-where-variant">
          {variantBound
            ? (lang === 'de' ? `Nur in der Version ${variant.label}` : `Only in version ${variant.label}`)
            : (lang === 'de' ? `Vor allem in der Version ${variant.label}` : `Mostly in version ${variant.label}`)}
        </p>
      )}
      {where.reach === 'templates' && (
        <ul className="oqc-where-list">
          {where.where.map((group) => (
            <li key={`${group.templateId}-${group.name}`}>
              <span>{group.name}</span>
              <HitStrip hits={group.hits} total={group.total} />
              <em>{group.hits}/{group.total}</em>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Hotspots({ rows, lang }) {
  if (!Array.isArray(rows) || rows.length < 2) return null;
  return (
    <section className="oqc-group oqc-hotspots">
      <div className="oqc-group-head">
        <h5>{lang === 'de' ? 'Wo es hängt' : 'Where it breaks'}</h5>
      </div>
      <ol className="oqc-hot-list">
        {rows.map((row) => (
          <li key={`${row.templateId}-${row.name}`} className={`oqc-hot oqc-hot--${row.worst}`}>
            <div className="oqc-hot-name">
              <strong>{row.name}</strong>
              {row.step ? <span>{row.step}</span> : null}
            </div>
            <HarmScale value={row.worst} />
            <div className="oqc-hot-mails">
              <HitStrip hits={row.flagged} total={row.mails} />
              <span>{row.flagged}/{row.mails} {lang === 'de' ? 'Mails' : 'emails'}</span>
            </div>
            <span className="oqc-hot-count">
              {row.findings} {lang === 'de' ? (row.findings === 1 ? 'Punkt' : 'Punkte') : (row.findings === 1 ? 'issue' : 'issues')}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

const quoteClose = (open) => {
  if (open === '„') return '“';
  if (open === '«') return '»';
  if (open === '"') return '"';
  if (open === '“') return '”';
  return '”';
};

const readQuoted = (text, from = 0) => {
  const start = text.slice(from).search(/[„"“«]/);
  if (start === -1) return null;
  const at = from + start;
  const open = text[at];
  const close = quoteClose(open);
  let depth = 1;
  for (let i = at + 1; i < text.length; i += 1) {
    if (open !== close && text[i] === open) depth += 1;
    else if (text[i] === close) {
      depth -= 1;
      if (depth === 0) return { body: text.slice(at + 1, i).trim(), end: i + 1, index: at };
    }
  }
  return null;
};

const lastQuote = (text) => {
  let found = null;
  let from = 0;
  while (from < text.length) {
    const next = readQuoted(text, from);
    if (!next) break;
    found = next;
    from = next.end;
  }
  return found;
};

const renderInlines = (text) => {
  const nodes = [];
  let from = 0;
  let key = 0;
  while (from < text.length) {
    const quote = readQuoted(text, from);
    if (!quote) {
      if (from < text.length) nodes.push(text.slice(from));
      break;
    }
    if (quote.index > from) nodes.push(text.slice(from, quote.index));
    nodes.push(<strong key={`q-${key}`} className="oqc-said">{text.slice(quote.index, quote.end)}</strong>);
    key += 1;
    from = quote.end;
  }
  return nodes;
};

const proseParts = (text) => {
  const paragraphs = String(text || '').split(/\n+/).map((part) => part.replace(/[ \t]+/g, ' ').trim()).filter(Boolean);
  return paragraphs.flatMap((paragraph) => {
    const sentences = paragraph.split(/(?<=[.!?])\s+(?=[A-ZÄÖÜ„"«])/);
    return sentences.length >= 3 ? sentences : [paragraph];
  });
};

function MarkedText({ text, className = 'oqc-prose' }) {
  const parts = proseParts(text);
  if (!parts.length) return null;
  return (
    <div className={className}>
      {parts.map((part, index) => <p key={`${index}-${part.slice(0, 24)}`}>{renderInlines(part)}</p>)}
    </div>
  );
}

const REPLACE_MARKER = /\s(?:ersetzen durch|replace with|ersetzen mit|austauschen gegen)\s*:?\s*/i;

const parseRewrite = (text) => {
  const raw = String(text || '').replace(/\s*\n+\s*/g, ' ').replace(/[ \t]{2,}/g, ' ').trim();
  if (!raw) return null;
  const pairs = [...raw.matchAll(/[„"“«]([^„"“”"»\n]+)[“"”»]\s*(?:→|->|=>)\s*[„"“«]([^„"“”"»\n]+)[“"”»]/g)];
  if (pairs.length >= 2) {
    let note = raw;
    pairs.forEach((match) => { note = note.replace(match[0], ' '); });
    note = note
      .replace(/\s+/g, ' ')
      .replace(/:\s*(?:,\s*)+\.?/g, '. ')
      .replace(/(?:,\s*){2,}/g, ' ')
      .replace(/\s+\./g, '.')
      .replace(/\s{2,}/g, ' ')
      .replace(/^folgende stellen korrigieren\.?\s*/i, '')
      .replace(/^[\s,;:.–—-]+|[\s,;:.–—-]+$/g, '')
      .trim();
    return { kind: 'list', pairs: pairs.map((match) => [match[1].trim(), match[2].trim()]), note };
  }
  const labeled = raw.match(/(?:Vorher|Before)\s*:\s*([\s\S]+?)\s+(?:Nachher|After)\s*:\s*([\s\S]+)/i);
  if (labeled) {
    const beforeQuote = lastQuote(labeled[1]) || readQuoted(labeled[1].trim());
    const afterQuote = readQuoted(labeled[2].trim());
    return {
      kind: 'swap',
      before: beforeQuote && beforeQuote.index === 0 ? beforeQuote.body : labeled[1].trim(),
      after: afterQuote && afterQuote.index === 0 ? afterQuote.body : labeled[2].trim(),
      tail: afterQuote && afterQuote.index === 0 ? labeled[2].trim().slice(afterQuote.end).trim() : '',
    };
  }
  const marker = raw.match(REPLACE_MARKER);
  if (marker && marker.index != null) {
    const head = raw.slice(0, marker.index);
    const rest = raw.slice(marker.index + marker[0].length).trim();
    const beforeQuote = lastQuote(head);
    const afterQuote = readQuoted(rest);
    const before = beforeQuote ? beforeQuote.body : head.replace(/^(?:den satz|the sentence)\s+/i, '').trim();
    if (before && afterQuote && afterQuote.body) {
      return { kind: 'swap', before, after: afterQuote.body, tail: rest.slice(afterQuote.end).trim() };
    }
    if (before && rest) return { kind: 'swap', before, after: rest, tail: '' };
  }
  return { kind: 'prose', text: raw };
};

function displayOf(item) {
  if (!item) return null;
  const pairs = (item.fixes || []).map((fix) => [fix.from, fix.to]).filter((pair) => pair[0] && pair[1]);
  const before = String(item.before || '').trim();
  const after = String(item.after || '').trim();
  const note = String(item.note || '').trim();
  if (item.shape === 'list' || pairs.length >= 2) {
    if (pairs.length) return { kind: 'list', pairs, tail: note };
  }
  if ((item.shape === 'swap' || (before && after)) && (before || after)) {
    return { kind: 'swap', before, after, tail: note };
  }
  if (item.shape === 'do' && after) {
    return { kind: 'do', after, tail: note };
  }
  return parseRewrite(after || item.change);
}

function Rewrite({ item, lang }) {
  const parsed = displayOf(item);
  if (!parsed) return null;
  if (parsed.kind === 'swap' && (parsed.before || parsed.after)) {
    return (
      <div className="oqc-rewrite">
        {parsed.before ? (
          <p className="oqc-rewrite-before">
            <span>{lang === 'de' ? 'So steht es jetzt' : 'As sent'}</span>
            {parsed.before}
          </p>
        ) : null}
        {parsed.after ? (
          <p className="oqc-rewrite-after">
            <span>{lang === 'de' ? 'Besser so' : 'Better'}</span>
            {renderInlines(parsed.after)}
          </p>
        ) : null}
        {parsed.tail ? <MarkedText text={parsed.tail} /> : null}
      </div>
    );
  }
  if (parsed.kind === 'list' && parsed.pairs && parsed.pairs.length) {
    return (
      <div className="oqc-rewrite">
        <ul className="oqc-swaps">
          {parsed.pairs.map(([from, to]) => (
            <li key={`${from}-${to}`}>
              <span>{from}</span>
              <i aria-hidden="true">→</i>
              <strong>{to}</strong>
            </li>
          ))}
        </ul>
        {parsed.tail ? <MarkedText text={parsed.tail} /> : null}
      </div>
    );
  }
  if (parsed.kind === 'do' && parsed.after) {
    return (
      <div className="oqc-rewrite">
        <p className="oqc-rewrite-after">
          <span>{lang === 'de' ? 'So behebst du es' : 'How to fix it'}</span>
          {renderInlines(parsed.after)}
        </p>
        {parsed.tail ? <MarkedText text={parsed.tail} /> : null}
      </div>
    );
  }
  return <MarkedText text={parsed.text || parsed.after || ''} />;
}

const viewOf = (item) => {
  if (!item) return 'note';
  if (item.view) return item.view;
  if (item.shape) return item.shape;
  if (item.left && item.right) return 'clash';
  if ((Number(item.count) >= 3) && item.quote) return 'spread';
  if (item.before && item.after) return 'swap';
  if (item.fixes && item.fixes.length >= 2) return 'list';
  if (item.quote) return 'quote';
  if (item.after || item.change) return 'do';
  return 'note';
};

function Exhibit({ item, lang, sampleSize }) {
  const view = viewOf(item);
  if (view === 'swap' || view === 'list' || view === 'do') {
    return <Rewrite item={{ ...item, shape: view }} lang={lang} />;
  }
  if (view === 'clash' && item.left && item.right) {
    return (
      <div className="oqc-clash">
        <div>
          <span>{item.leftLabel || (lang === 'de' ? 'In der Mail' : 'In the email')}</span>
          <strong>{item.left}</strong>
        </div>
        <div>
          <span>{item.rightLabel || (lang === 'de' ? 'Gespeichert' : 'On file')}</span>
          <b>{item.right}</b>
        </div>
      </div>
    );
  }
  if (view === 'spread') {
    const times = (item.where && item.where.mails) || Number(item.count) || 0;
    const sample = (item.where && item.where.sample) || Number(sampleSize) || 0;
    return (
      <div className="oqc-spread">
        {times > 1 && (
          <p className="oqc-spread-n">
            {times}
            {sample > 0 ? <span>{lang === 'de' ? 'von' : 'of'} {sample}</span> : null}
          </p>
        )}
        {item.quote ? <p className="oqc-quote">{item.quote}</p> : null}
      </div>
    );
  }
  if (item.quote) {
    return (
      <p className="oqc-quote">
        <span>{lang === 'de' ? 'So steht es' : 'As sent'}</span>
        {item.quote}
      </p>
    );
  }
  return null;
}

const sentDay = (ms, lang) => (ms ? new Date(ms).toLocaleDateString(lang === 'en' ? 'en-US' : 'de-DE', {
  weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/Berlin',
}) : '');

const hasSnippet = (item) => Boolean(item && Array.isArray(item.evidence) && item.evidence[0] && item.evidence[0].span);

function Highlighted({ text, span }) {
  const value = String(text || '');
  if (!span || !(span.end > span.start) || span.end > value.length) return value;
  return (
    <>
      {value.slice(0, span.start)}
      <mark className="oqc-hit">{value.slice(span.start, span.end)}</mark>
      {value.slice(span.end)}
    </>
  );
}

function EvidenceMail({ entry, mail, lang }) {
  const de = lang === 'de';
  const full = mail && mail.text ? String(mail.text) : '';
  const meta = [
    entry.step,
    entry.template && entry.template !== entry.step ? `„${entry.template}“` : '',
    entry.variant,
    sentDay(entry.sentAt, lang),
  ].filter(Boolean).join(' · ');
  const preview = !entry.snippet && full ? `${full.slice(0, 260).trim()}${full.length > 260 ? '…' : ''}` : '';
  return (
    <div className="oqc-mail">
      <p className="oqc-mail-head">
        <strong>{de ? 'An' : 'To'} {entry.who || entry.toEmail || (de ? 'unbekannt' : 'unknown')}</strong>
        {entry.who && entry.toEmail ? <span>{entry.toEmail}</span> : null}
      </p>
      {meta ? <p className="oqc-mail-meta">{meta}</p> : null}
      {entry.subject ? (
        <p className="oqc-mail-subject">
          <span>{de ? 'Betreff' : 'Subject'}</span>
          <Highlighted text={entry.subject} span={entry.field === 'subject' ? entry.span : null} />
        </p>
      ) : null}
      {entry.snippet ? (
        <p className="oqc-mail-snippet">
          {entry.snippet.before}
          <mark className="oqc-hit">{entry.snippet.hit}</mark>
          {entry.snippet.after}
        </p>
      ) : null}
      {preview ? <p className="oqc-mail-snippet oqc-mail-snippet--plain">{preview}</p> : null}
      {full ? (
        <details className="oqc-mail-full">
          <summary>{de ? 'Ganze Mail lesen' : 'Read the full email'}</summary>
          <div className="oqc-mail-text">
            <Highlighted text={full} span={entry.field === 'text' ? entry.span : null} />
          </div>
        </details>
      ) : null}
    </div>
  );
}

const keepToCard = (event) => event.stopPropagation();

function Evidence({ item, mails, lang }) {
  const list = Array.isArray(item && item.evidence) ? item.evidence.filter(Boolean) : [];
  if (!list.length) return null;
  const de = lang === 'de';
  const [first, ...rest] = list;
  return (
    <div className="oqc-evidence" onClick={keepToCard} onKeyDown={keepToCard}>
      <p className="oqc-label">{de ? 'Hier ist es passiert' : 'Where it happened'}</p>
      <EvidenceMail entry={first} mail={mails && mails[first.ref]} lang={lang} />
      {rest.length > 0 && (
        <details className="oqc-evidence-more">
          <summary>
            {de
              ? `${rest.length} ${rest.length === 1 ? 'weiteres Beispiel' : 'weitere Beispiele'}`
              : `${rest.length} more ${rest.length === 1 ? 'example' : 'examples'}`}
          </summary>
          {rest.map((entry) => <EvidenceMail key={entry.ref} entry={entry} mail={mails && mails[entry.ref]} lang={lang} />)}
        </details>
      )}
    </div>
  );
}

const FIX_PLACES = {
  de: {
    template: 'Im Template',
    footer: 'In den Footer-Einstellungen',
    konto: 'In den Einstellungen des E-Mail-Kontos',
    kontakt: 'In den Kontaktdaten der Empfänger',
    liste: 'In der Empfängerliste der Kampagne',
    text: 'Nur in dieser einen Mail',
  },
  en: {
    template: 'In the template',
    footer: 'In the footer settings',
    konto: 'In the email account settings',
    kontakt: 'In the recipients’ contact details',
    liste: 'In the campaign’s recipient list',
    text: 'Only in this one email',
  },
};

function FixIn({ item, lang }) {
  const place = item && (FIX_PLACES[lang] || FIX_PLACES.de)[item.fixIn];
  if (!place) return null;
  const top = item.where && item.where.where && item.where.where[0];
  const named = item.fixIn === 'template' && top && top.templateId ? ` „${top.name}“` : '';
  return (
    <p className="oqc-fixin">
      <span>{lang === 'de' ? 'Wo du es änderst' : 'Where to change it'}</span>
      {place}{named}
    </p>
  );
}

function Reason({ text, lang }) {
  const value = String(text || '').trim();
  if (!value) return null;
  return (
    <p className="oqc-reason">
      <span>{lang === 'de' ? 'Warum das wichtig ist' : 'Why it matters'}</span>
      {renderInlines(value)}
    </p>
  );
}

function Markable({ marked, onToggle, lang, className, as: Tag = 'article', children }) {
  return (
    <Tag
      className={`${className}${marked ? ' oqc-point--mark' : ''}`}
      role="button"
      tabIndex={0}
      aria-pressed={Boolean(marked)}
      title={lang === 'de'
        ? (marked ? 'Markierung entfernen' : 'Für den Druck markieren')
        : (marked ? 'Remove mark' : 'Mark for print')}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onToggle();
      }}
    >
      {marked ? <span className="oqc-mark" aria-hidden="true">💡</span> : null}
      {children}
    </Tag>
  );
}

function FindingCard({ item, sampleSize, mails, lang, marked, onToggle }) {
  const title = cardTitle(item);
  const view = viewOf(item);
  const fact = String(item.fact || '').trim();
  const problem = String(item.problem || '').trim();
  const impact = String(item.impact || '').trim();
  const detail = problem && problem !== title ? problem : '';
  const extra = view === 'spread' ? '' : (fact && fact !== problem && fact !== title ? fact : '');
  const share = (view === 'spread' || item.where) ? '' : shareLabel(item.count, sampleSize, lang);
  const showExhibit = !(view === 'quote' && hasSnippet(item));
  return (
    <Markable
      marked={marked}
      onToggle={onToggle}
      lang={lang}
      className={`oqc-point oqc-finding oqc-view--${view} oqc-finding--${item.severity || 'mittel'}${item.where ? ` oqc-reach--${item.where.reach}` : ''}`}
    >
      <div className="oqc-point-top">
        <PointNo n={item.n} />
        <HarmScale value={item.severity} />
        {share && <span className="oqc-count">{share}</span>}
        {item.who && <span className="oqc-who">{item.who}</span>}
      </div>
      <div className="oqc-point-body">
        <p className="oqc-problem">{title}</p>
        {detail && <p className="oqc-what">{renderInlines(detail)}</p>}
        {showExhibit && <Exhibit item={item} lang={lang} sampleSize={sampleSize} />}
        <Evidence item={item} mails={mails} lang={lang} />
        <Reason text={impact} lang={lang} />
        {extra && <p className="oqc-source">{extra}</p>}
        <WhereLine where={item.where} lang={lang} />
      </div>
    </Markable>
  );
}

function SuggestionBody({ item, mails, lang }) {
  const problem = String(item.problem || '').trim();
  return (
    <div className="oqc-point-body">
      <p className="oqc-problem">{item.title}</p>
      {problem && problem !== item.title && <p className="oqc-what">{renderInlines(problem)}</p>}
      <Evidence item={item} mails={mails} lang={lang} />
      {(item.change || item.after || (item.fixes && item.fixes.length)) && <Rewrite item={item} lang={lang} />}
      <FixIn item={item} lang={lang} />
      <Reason text={item.why} lang={lang} />
      <WhereLine where={item.where} lang={lang} />
    </div>
  );
}

function FindingGroup({ title, items, sampleSize, mails, startAt = 1, lang, marked, onToggle }) {
  if (!items.length) return null;
  const ranked = items.filter((item) => item.severity !== 'niedrig');
  const small = items.filter((item) => item.severity === 'niedrig');
  const shown = (ranked.length ? ranked : items).slice(0, 4);
  const folded = [
    ...(ranked.length > 4 ? ranked.slice(4) : []),
    ...(ranked.length ? small : items.slice(4)),
  ];
  let cursor = startAt;
  const stamp = (list) => list.map((item) => ({ ...item, n: cursor++ }));
  const shownN = stamp(shown);
  const foldedN = stamp(folded);
  return (
    <section className="oqc-group">
      <div className="oqc-group-head">
        <h5>{title}</h5>
        <span className="oqc-count-pill">{items.length}</span>
      </div>
      <div className="oqc-finding-list">
        {shownN.map((item) => (
          <FindingCard key={`${item.n}-${item.problem}`} item={item} sampleSize={sampleSize} mails={mails} lang={lang} marked={Boolean(marked && marked[item.n])} onToggle={() => onToggle(item.n)} />
        ))}
      </div>
      {foldedN.length > 0 && (
        <details className="oqc-fold">
          <summary>{lang === 'de' ? `${foldedN.length} weitere in ${title}` : `${foldedN.length} more in ${title}`}</summary>
          <div className="oqc-finding-list">
            {foldedN.map((item) => (
              <FindingCard key={`more-${item.n}-${item.problem}`} item={item} sampleSize={sampleSize} mails={mails} lang={lang} marked={Boolean(marked && marked[item.n])} onToggle={() => onToggle(item.n)} />
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

const berlinMonthKey = (date = new Date()) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
}).format(date);

const reportsRefOf = (userId) => collection(firestore, 'world_analytics', userId, 'quality_reports');

const asReport = (id, data) => {
  const report = { id, ...data };
  if (report.createdAt?.toDate) {
    const date = report.createdAt.toDate();
    report.createdAtMs = report.createdAtMs || date.getTime();
    report.createdAt = date.toISOString();
  }
  const rated = scoreReport(report.findings, report.avgServerScore);
  if (typeof report.score !== 'number') report.score = rated.score;
  if (!Array.isArray(report.dimensions) || report.dimensions.length === 0) report.dimensions = rated.dimensions;
  return report;
};

const listScore = (data) => (
  typeof data.score === 'number' ? data.score : scoreReport(data.findings, data.avgServerScore).score
);

const REPLY_BUCKETS = [
  { id: 'hour', de: 'Innerhalb einer Stunde', en: 'Within an hour' },
  { id: 'day', de: 'Am selben Tag', en: 'Same day' },
  { id: 'next', de: 'Am nächsten Tag', en: 'Next day' },
  { id: 'later', de: 'Später', en: 'Later' },
];

const formatWait = (ms, lang) => {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '';
  const minutes = Math.round(n / 60000);
  if (minutes < 1) return lang === 'de' ? 'unter einer Minute' : 'under a minute';
  if (minutes < 90) return lang === 'de' ? `${minutes} Min.` : `${minutes} min`;
  const hours = n / 3600000;
  if (hours < 36) {
    const rounded = hours < 10 ? Math.round(hours * 10) / 10 : Math.round(hours);
    const text = String(rounded).replace('.', lang === 'de' ? ',' : '.');
    return lang === 'de' ? `${text} Std.` : `${text} h`;
  }
  const days = n / 86400000;
  const shown = days < 10 ? Math.round(days * 10) / 10 : Math.round(days);
  if (shown === 1) return lang === 'de' ? '1 Tag' : '1 day';
  const text = String(shown).replace('.', lang === 'de' ? ',' : '.');
  return lang === 'de' ? `${text} Tage` : `${text} days`;
};

function ReplyTimePanel({ userId, accountEmail, language }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const lang = language === 'en' ? 'en' : 'de';

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setData(null);
    const call = httpsCallable(functions, 'getOutreachReplyTime', { timeout: 60000 });
    call({ worldId: userId, accountEmail: accountEmail || 'all' })
      .then((result) => {
        if (cancelled) return;
        const next = result && result.data;
        setData(next && typeof next.incoming === 'number' ? next : { incoming: 0, answered: 0, marked: 0, open: 0, buckets: {} });
      })
      .catch((err) => {
        console.warn('[oqc] reply time failed:', err?.message);
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, accountEmail]);

  const scope = accountEmail && accountEmail !== 'all'
    ? `, ${accountEmail}`
    : (lang === 'de' ? ', alle Postfächer' : ', all accounts');
  const intro = lang === 'de'
    ? `Letzte 30 Tage${scope}. Uhrzeit, inklusive Nacht und Wochenende.`
    : `Last 30 days${scope}. Clock time, including nights and weekends.`;

  const headline = (() => {
    if (!data || !data.incoming) return null;
    if (typeof data.medianMs === 'number') {
      return {
        value: formatWait(data.medianMs, lang),
        label: lang === 'de' ? 'Median, bis die Antwort raus war' : 'median until the reply went out',
      };
    }
    if (typeof data.markedMedianMs === 'number') {
      return {
        value: formatWait(data.markedMedianMs, lang),
        label: lang === 'de' ? 'Median, bis sie als beantwortet markiert wurde' : 'median until it was marked answered',
      };
    }
    if (data.open > 0 && typeof data.oldestOpenMs === 'number') {
      return {
        value: formatWait(data.oldestOpenMs, lang),
        label: lang === 'de' ? 'älteste wartet seit' : 'oldest is still waiting',
      };
    }
    return null;
  })();

  const foot = (() => {
    if (!data || !data.incoming) return '';
    const parts = [];
    if (data.answered > 0) {
      parts.push(lang === 'de'
        ? `${data.answered} von ${data.incoming} mit gesendeter Antwort`
        : `${data.answered} of ${data.incoming} answered with a sent reply`);
    } else {
      parts.push(lang === 'de'
        ? `${data.incoming} eingegangen, noch keine gesendete Antwort`
        : `${data.incoming} came in, no sent reply yet`);
    }
    if (data.open > 0 && data.open !== data.incoming) {
      const oldest = formatWait(data.oldestOpenMs, lang);
      const withAge = Boolean(oldest) && (typeof data.medianMs === 'number' || typeof data.markedMedianMs === 'number');
      parts.push(lang === 'de'
        ? `${data.open} noch offen${withAge ? `, älteste seit ${oldest}` : ''}`
        : `${data.open} still waiting${withAge ? `, oldest ${oldest}` : ''}`);
    }
    return parts.join(' · ');
  })();

  const buckets = (data && data.buckets) || {};
  const answered = data ? Number(data.answered) || 0 : 0;

  return (
    <section className="oqc-reply" aria-live="polite">
      <div className="oqc-reply-head">
        <h5>{lang === 'de' ? 'Antwortzeit' : 'Reply time'}</h5>
      </div>
      <p className="oqc-reply-sub">{intro}</p>
      {loading && <p className="oqc-reply-sub">{lang === 'de' ? 'Wird geladen…' : 'Loading…'}</p>}
      {failed && !loading && (
        <p className="oqc-reply-sub">{lang === 'de' ? 'Antwortzeiten konnten nicht geladen werden.' : 'Reply times could not be loaded.'}</p>
      )}
      {data && data.incoming === 0 && !loading && (
        <p className="oqc-reply-sub">{lang === 'de' ? 'Keine eingegangenen Antworten in den letzten 30 Tagen.' : 'No incoming replies in the last 30 days.'}</p>
      )}
      {headline && (
        <div className="oqc-reply-body">
          <div className="oqc-reply-median">
            <strong>{headline.value}</strong>
            <span>{headline.label}</span>
          </div>
          {answered > 0 && (
            <ul className="oqc-reply-buckets">
              {REPLY_BUCKETS.map((bucket) => {
                const count = Number(buckets[bucket.id]) || 0;
                const width = answered ? Math.round((count / answered) * 100) : 0;
                return (
                  <li key={bucket.id}>
                    <span>{lang === 'de' ? bucket.de : bucket.en}</span>
                    <b>{count}</b>
                    <span className="oqc-reply-track" aria-hidden="true">
                      <i className="oqc-reply-fill" style={{ width: `${width}%` }} />
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {foot && <p className="oqc-reply-foot">{foot}</p>}
      {data && data.marked > 0 && (
        <p className="oqc-reply-foot">
          {lang === 'de'
            ? `${data.marked} nur als beantwortet markiert, ohne gesendete Antwort im Thread.`
            : `${data.marked} marked answered, with no sent reply on the thread.`}
        </p>
      )}
      {data && data.capped && (
        <p className="oqc-reply-foot">
          {lang === 'de'
            ? 'Gezählt wurden die neuesten 800 Mails im Posteingang.'
            : 'Counted from the newest 800 inbox mails.'}
        </p>
      )}
    </section>
  );
}

function OutreachQualityCheck({ userId }) {
  const [accounts, setAccounts] = useState([]);
  const [accountEmail, setAccountEmail] = useState('all');
  const [reportName, setReportName] = useState('');
  const [language, setLanguage] = useState(() => {
    try {
      return window.localStorage.getItem('oqc-report-language') === 'en' ? 'en' : 'de';
    } catch (_) {
      return 'de';
    }
  });
  const [savedReports, setSavedReports] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [quota, setQuota] = useState({ used: 0, limit: 4 });
  const [running, setRunning] = useState(false);
  const [marked, setMarked] = useState({});
  const [progress, setProgress] = useState(null);
  const runStartedRef = useRef(0);
  const reportRef = useRef(null);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(() => {
    try { window.localStorage.setItem('oqc-report-language', language); } catch (_) {}
  }, [language]);

  useEffect(() => {
    if (report && (report.language === 'en' || report.language === 'de')) setLanguage(report.language);
  }, [report]);

  useEffect(() => {
    if (!userId) return undefined;
    let creditPlan = null;
    let membershipPlan = null;
    let alive = true;
    const applyPlan = () => {
      if (!alive) return;
      const planId = resolveMembershipPlanId(creditPlan, { membershipPlan });
      setQuota((prev) => ({ ...prev, limit: qualityChecksPerMonth(planId) }));
    };
    const unsubCredit = onValue(ref(database, `World/${userId}/creditPlan`), (snap) => {
      creditPlan = snap.exists() ? snap.val() : null;
      applyPlan();
    });
    const unsubMember = onValue(ref(database, `Users/${userId}/membershipPlan`), (snap) => {
      membershipPlan = snap.exists() ? snap.val() : null;
      applyPlan();
    });
    getDoc(doc(firestore, 'world_analytics', userId, 'quality_quota', berlinMonthKey()))
      .then((snap) => {
        if (!alive || !snap.exists()) return;
        setQuota((prev) => ({ ...prev, used: Number(snap.data().used) || 0 }));
      })
      .catch((err) => console.warn('[oqc] quota failed:', err.message));
    return () => {
      alive = false;
      unsubCredit();
      unsubMember();
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    const accountsRef = collection(firestore, 'world_users', userId, 'emailAccounts');
    const unsub = onSnapshot(accountsRef, (snap) => {
      const list = snap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((account) => account.email)
        .sort((a, b) => String(a.email).localeCompare(String(b.email)));
      setAccounts(list);
    });
    return () => unsub();
  }, [userId]);

  const loadSavedReports = async () => {
    if (!userId) return;
    setLoadingSaved(true);
    try {
      const snap = await getDocs(query(reportsRefOf(userId), orderBy('createdAt', 'desc'), limit(20)));
      setSavedReports(snap.docs.map((item) => {
        const data = item.data();
        return {
          id: item.id,
          name: data.name || null,
          periodLabel: data.periodLabel || '',
          accountLabel: data.accountLabel || '',
          sampleSize: data.sampleSize || 0,
          score: listScore(data),
          createdAtMs: data.createdAtMs || (data.createdAt && data.createdAt.toMillis ? data.createdAt.toMillis() : 0),
        };
      }));
    } catch (err) {
      console.warn('[oqc] saved list failed:', err.message);
    } finally {
      setLoadingSaved(false);
    }
  };

  const openSavedReport = async (reportId) => {
    if (!userId || !reportId) return;
    setSelectedId(reportId);
    setError('');
    try {
      const snap = await getDoc(doc(firestore, 'world_analytics', userId, 'quality_reports', reportId));
      if (!snap.exists()) throw new Error('Report not found.');
      setReport(asReport(snap.id, snap.data()));
    } catch (err) {
      setError(err.message || 'Saved report could not be loaded.');
    }
  };

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setLoadingSaved(true);
    getDocs(query(reportsRefOf(userId), orderBy('createdAt', 'desc'), limit(20)))
      .then((snap) => {
        if (cancelled) return;
        const list = snap.docs.map((item) => {
          const data = item.data();
          return {
            id: item.id,
            name: data.name || null,
            periodLabel: data.periodLabel || '',
            accountLabel: data.accountLabel || '',
            sampleSize: data.sampleSize || 0,
            score: listScore(data),
            createdAtMs: data.createdAtMs || (data.createdAt && data.createdAt.toMillis ? data.createdAt.toMillis() : 0),
          };
        });
        setSavedReports(list);
        if (snap.docs[0]) {
          setSelectedId(snap.docs[0].id);
          setReport(asReport(snap.docs[0].id, snap.docs[0].data()));
        }
      })
      .catch((err) => console.warn('[oqc] saved list failed:', err.message))
      .finally(() => { if (!cancelled) setLoadingSaved(false); });
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    if (!userId || !running) return undefined;
    const unsub = onSnapshot(doc(firestore, 'world_analytics', userId, 'quality_status', 'current'), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      const at = data.updatedAt && data.updatedAt.toMillis ? data.updatedAt.toMillis() : 0;
      if (at && at + 20000 < runStartedRef.current) return;
      if (data.step && data.step !== 'done') setProgress({ step: data.step, detail: data.detail || '' });
    });
    return () => unsub();
  }, [userId, running]);

  const run = async () => {
    if (!userId || running) return;
    runStartedRef.current = Date.now();
    setProgress({ step: 'sample', detail: 'Pulling emails from the last 30 days.' });
    setRunning(true);
    setError('');
    setSelectedId('');
    try {
      const call = httpsCallable(functions, 'runOutreachQualityCheck', { timeout: 540000 });
      const result = await call({
        worldId: userId,
        accountEmail,
        name: reportName.trim(),
        language,
      });
      const next = result.data ? asReport(result.data.id, result.data) : null;
      setReport(next);
      if (next && next.id) setSelectedId(next.id);
      if (next && next.quota) {
        setQuota({ used: Number(next.quota.used) || 0, limit: Number(next.quota.limit) || 4 });
      }
      setReportName('');
      loadSavedReports();
    } catch (err) {
      const message = err?.message || 'The check failed.';
      setError(message.replace(/^INTERNAL:\s*/, ''));
    } finally {
      setRunning(false);
    }
  };

  const printReport = () => {
    if (!report) return;
    const previousTitle = document.title;
    const label = report.name || report.verdict || 'Quality Check';
    document.title = `Quality Check — ${String(label).replace(/\s+/g, ' ').trim().slice(0, 80)}`;
    document.body.setAttribute('data-an-print', 'quality-check');
    const details = reportRef.current ? Array.from(reportRef.current.querySelectorAll('details:not(.oqc-mail-full)')) : [];
    const previousOpen = details.map((node) => node.open);
    details.forEach((node) => { node.open = true; });
    let cleared = false;
    const clear = () => {
      if (cleared) return;
      cleared = true;
      document.body.removeAttribute('data-an-print');
      document.title = previousTitle;
      details.forEach((node, index) => { node.open = previousOpen[index]; });
      window.removeEventListener('afterprint', clear);
    };
    window.addEventListener('afterprint', clear);
    setTimeout(clear, 60000);
    requestAnimationFrame(() => window.print());
  };

  const deleteSavedReport = async (reportId) => {
    if (!userId || !reportId) return;
    if (!window.confirm('Delete this report?')) return;
    setDeletingId(reportId);
    try {
      await deleteDoc(doc(firestore, 'world_analytics', userId, 'quality_reports', reportId));
      setSavedReports((prev) => prev.filter((item) => item.id !== reportId));
      if (selectedId === reportId) {
        setSelectedId('');
        setReport(null);
      }
    } catch (err) {
      setError(err.message || 'Report could not be deleted.');
    } finally {
      setDeletingId('');
    }
  };

  const remainingRuns = Math.max(0, quota.limit - quota.used);
  const quotaLine = remainingRuns === 0
    ? 'No generations left this month.'
    : `${remainingRuns} of ${quota.limit} generations left this month.`;
  const failedLanes = ((report && report.lanes) || []).filter((lane) => lane && lane.ok === false);
  const when = report && report.createdAtMs
    ? new Date(report.createdAtMs).toLocaleString(report.language === 'en' ? 'en-US' : 'de-DE', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
    : '';
  const earlier = report
    ? savedReports.find((item) => item.id !== report.id && typeof item.score === 'number')
    : null;
  const scoreDelta = earlier && typeof report.score === 'number' ? report.score - earlier.score : null;
  const suggestions = Array.isArray(report && report.suggestions) ? report.suggestions : [];
  const suggestionPoints = suggestions.map((item, index) => ({ ...item, n: index + 1 }));
  const lead = suggestionPoints[0] || null;
  const laterSuggestions = suggestionPoints.slice(1);
  const findingStart = suggestionPoints.length + 1;
  const reportLang = report && report.language === 'en' ? 'en' : 'de';
  const reportId = report && report.id;
  useEffect(() => { setMarked({}); }, [reportId]);
  const toggleMark = (n) => setMarked((prev) => ({ ...prev, [n]: !prev[n] }));
  const readLines = report ? [
    report.arrival && { label: reportLang === 'de' ? 'So wirkt es' : 'How it lands', text: firstSentence(report.arrival) },
    report.repetition && { label: reportLang === 'de' ? 'Was sich wiederholt' : 'What repeats', text: firstSentence(report.repetition) },
    report.trust && { label: reportLang === 'de' ? 'Wo es kippt' : 'Where it breaks', text: firstSentence(report.trust) },
  ].filter((line) => line && line.text) : [];
  const fullRead = [report && report.arrival, report && report.repetition, report && report.trust]
    .filter((text) => text && text.length > firstSentence(text).length + 40);

  return (
    <section className="an-card oqc">
      <div className="an-bookings-head">
        <div>
          <h4 className="an-section-title">Email Outreach Quality Check</h4>
          <p className="an-section-sub">
            Drawn fresh from your latest 200 sent emails: up to 30 intros plus 10 from each follow-up step,
            30 to 50 emails in total. Every finding shows the email where it happened.
          </p>
        </div>
      </div>

      <div className="an-controls">
        <div className="an-field an-field--grow">
          <label htmlFor="oqc-name">Name</label>
          <input
            id="oqc-name"
            type="text"
            className="an-input"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            placeholder="Optional, e.g. October campaign"
            maxLength={50}
            disabled={running}
          />
        </div>
        <div className="an-field oqc-lang">
          <span id="oqc-lang-label">Report language</span>
          <div className="oqc-lang-switch" role="group" aria-labelledby="oqc-lang-label">
            <button type="button" className={language === 'de' ? 'is-on' : ''} aria-pressed={language === 'de'} onClick={() => setLanguage('de')} disabled={running}>Deutsch</button>
            <button type="button" className={language === 'en' ? 'is-on' : ''} aria-pressed={language === 'en'} onClick={() => setLanguage('en')} disabled={running}>English</button>
          </div>
        </div>
        <div className="an-field an-field--grow">
          <label htmlFor="oqc-account">Account</label>
          <select id="oqc-account" className="an-input" value={accountEmail} onChange={(e) => setAccountEmail(e.target.value)} disabled={running}>
            <option value="all">All accounts</option>
            {accounts.map((account) => (
              <option key={account.id} value={String(account.email).toLowerCase()}>
                {account.email}{account.sender_name ? ` · ${account.sender_name}` : ''}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className="altBtn altBtn--save" onClick={run} disabled={running || !userId || quota.used >= quota.limit}>
          {running ? 'Running…' : quota.used >= quota.limit ? 'Used up this month' : 'Start check'}
        </button>
      </div>
      <p className="oqc-note">
        {quotaLine}
        {' '}A run takes one to two minutes.
      </p>

      <ReplyTimePanel userId={userId} accountEmail={accountEmail} language={language} />

      {running && (
        <ol className="oqc-progress" aria-live="polite">
          {PROGRESS_STEPS.map((item, index) => {
            const current = PROGRESS_STEPS.findIndex((step) => step.id === (progress && progress.step));
            const state = index < current ? 'done' : index === current ? 'active' : 'wait';
            const detail = state === 'active' && progress && progress.detail
              ? progressDetail(progress.detail, item.text)
              : item.text;
            return (
              <li key={item.id} className={`oqc-progress-step oqc-progress-step--${state}`}>
                <span className="oqc-progress-mark" aria-hidden>{state === 'done' ? '✓' : index + 1}</span>
                <span>
                  <strong>{item.title}</strong>
                  <em>{detail}</em>
                </span>
              </li>
            );
          })}
        </ol>
      )}

      <div className="an-saved">
        <label htmlFor="oqc-saved">Saved reports</label>
        <div className="an-saved-row">
          <select
            id="oqc-saved"
            className="an-input"
            value={selectedId}
            onChange={(e) => {
              const id = e.target.value;
              if (id) openSavedReport(id);
              else setSelectedId('');
            }}
            disabled={running || loadingSaved}
          >
            {loadingSaved ? (
              <option value="">Loading…</option>
            ) : savedReports.length === 0 ? (
              <option value="">None yet — create one above</option>
            ) : (
              <>
                <option value="">Open a saved report</option>
                {savedReports.map((item) => (
                  <option key={item.id} value={item.id}>
                    {typeof item.score === 'number' ? `${item.score} · ` : ''}
                    {item.name ? `${item.name} · ` : ''}
                    {item.periodLabel}
                    {item.accountLabel ? ` · ${item.accountLabel}` : ''}
                    {item.sampleSize ? ` · ${item.sampleSize} emails` : ''}
                  </option>
                ))}
              </>
            )}
          </select>
          {selectedId && (
            <button
              type="button"
              className="altBtn altBtn--petrol"
              onClick={() => deleteSavedReport(selectedId)}
              disabled={deletingId === selectedId || running}
            >
              {deletingId === selectedId ? '…' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="oqc-error">{error}</div>}

      {report && !running && (
        <div className="oqc-report" ref={reportRef}>
          <div className="oqc-report-tools oqc-screen-only">
            <span className="oqc-mark-hint">{reportLang === 'de' ? 'Punkt anklicken, um ihn für den Druck zu markieren.' : 'Click a point to mark it before printing.'}</span>
            <button type="button" className="altBtn altBtn--petrol" onClick={printReport}>
              {reportLang === 'de' ? 'Drucken / PDF' : 'Print / PDF'}
            </button>
          </div>
          <div className="oqc-print-banner">
            <p>Email Outreach Quality Check</p>
            {report.name ? <strong>{report.name}</strong> : null}
          </div>
          <header className="oqc-score">
            <div className={`oqc-score-num oqc-score-num--${scoreTone(report.score)}`}>
              <strong>{report.score}</strong>
              <span>{reportLang === 'de' ? 'von 100' : 'of 100'}</span>
            </div>
            <div className="oqc-score-copy">
              <strong>{report.verdict || accountLine(report.accountLabel, reportLang) || (reportLang === 'de' ? 'Stichprobe' : 'Sample')}</strong>
              <span>
                {report.sampleSize} {reportLang === 'de' ? 'Mails' : 'emails'}
                {report.accountLabel ? ` · ${accountLine(report.accountLabel, reportLang)}` : ''}
                {when ? ` · ${when}` : ''}
              </span>
              {compositionText(report, reportLang) && <span>{compositionText(report, reportLang)}</span>}
              <span className="oqc-screen-only">{quotaLine}</span>
              {scoreDelta !== null && (
                <span className={`oqc-delta oqc-delta--${scoreDelta > 0 ? 'up' : scoreDelta < 0 ? 'down' : 'flat'}`}>
                  {scoreDelta > 0
                    ? (reportLang === 'de' ? `${scoreDelta} Punkte über` : `${scoreDelta} points above`)
                    : scoreDelta < 0
                      ? (reportLang === 'de' ? `${Math.abs(scoreDelta)} Punkte unter` : `${Math.abs(scoreDelta)} points below`)
                      : (reportLang === 'de' ? 'Gleichauf mit' : 'Level with')} {earlier.name || earlier.periodLabel || (reportLang === 'de' ? 'dem letzten Scan' : 'the last scan')}
                </span>
              )}
            </div>
            {report.inboxPlacement && (
              <span className={`oqc-place oqc-place--${report.inboxPlacement}`}>
                {(PLACEMENT_BY_LANG[reportLang] || PLACEMENT_BY_LANG.de)[report.inboxPlacement] || report.inboxPlacement}
              </span>
            )}
          </header>

          {report.audience && <MarkedText text={report.audience} className="oqc-prose oqc-audience" />}

          <div className="oqc-meters">
            {(report.dimensions || []).map((dim) => (
              <div key={dim.id} className="oqc-meter">
                <div className="oqc-meter-top">
                  <span>{dimensionTitle(dim, reportLang)}</span>
                  <strong className={`oqc-tone oqc-tone--${scoreTone(dim.score)}`}>{dim.score}</strong>
                </div>
                <div className="oqc-meter-track">
                  <div className={`oqc-meter-fill oqc-meter-fill--${scoreTone(dim.score)}`} style={{ width: `${dim.score}%` }} />
                </div>
                <p className="oqc-meter-note">{meterLine(dim, report.findings, report.sampleSize, reportLang)}</p>
              </div>
            ))}
          </div>

          <Hotspots rows={report.hotspots} lang={reportLang} />

          {lead && (
            <Markable marked={Boolean(marked[lead.n])} onToggle={() => toggleMark(lead.n)} lang={reportLang} as="section" className={`oqc-point oqc-lead oqc-view--${viewOf(lead)}`}>
              <div className="oqc-point-top">
                <PointNo n={lead.n} />
                <HarmScale value={lead.urgency} />
                <span className="oqc-count">{reportLang === 'de' ? 'Zuerst ändern' : 'Change this first'}</span>
              </div>
              <SuggestionBody item={lead} mails={report.mails} lang={reportLang} />
            </Markable>
          )}

          {laterSuggestions.length > 0 && (
            <section className="oqc-group">
              <div className="oqc-group-head">
                <h5>{reportLang === 'de' ? 'Danach' : 'Next'}</h5>
                <span className="oqc-count-pill">{laterSuggestions.length}</span>
              </div>
              <div className="oqc-finding-list">
                {laterSuggestions.map((item) => (
                  <Markable key={`${item.n}-${item.title}`} marked={Boolean(marked[item.n])} onToggle={() => toggleMark(item.n)} lang={reportLang} className={`oqc-point oqc-suggestion oqc-view--${viewOf(item)}`}>
                    <div className="oqc-point-top">
                      <PointNo n={item.n} />
                      <HarmScale value={item.urgency} />
                    </div>
                    <SuggestionBody item={item} mails={report.mails} lang={reportLang} />
                  </Markable>
                ))}
              </div>
            </section>
          )}

          {Array.isArray(report.readers) && report.readers.length > 0 && (
            <section className="oqc-group">
              <div className="oqc-group-head"><h5>{reportLang === 'de' ? 'So lesen es zwei Empfänger' : 'How two recipients read it'}</h5></div>
              <div className="oqc-finding-list">
                {report.readers.map((item) => (
                  <article key={item.who} className="oqc-reader">
                    <span className="oqc-count">{item.who}</span>
                    <p>„{item.reaction}“</p>
                  </article>
                ))}
              </div>
            </section>
          )}

          {Array.isArray(report.strengths) && report.strengths.length > 0 && (
            <section className="oqc-group">
              <div className="oqc-group-head">
                <h5>{reportLang === 'de' ? 'Was trägt, bleibt' : 'What already works'}</h5>
                <span className="oqc-count-pill">{report.strengths.length}</span>
              </div>
              <div className="oqc-finding-list">
                {report.strengths.map((item) => (
                  <article key={item.title} className="oqc-strength">
                    <p className="oqc-problem">{item.title}</p>
                    {item.why && <MarkedText text={item.why} className="oqc-prose oqc-why" />}
                    {item.quote && <p className="oqc-quote">{item.quote}</p>}
                  </article>
                ))}
              </div>
            </section>
          )}

          {readLines.length > 0 && (
            <section className="oqc-read">
              {readLines.map((line) => (
                <p key={line.label}><strong>{line.label}.</strong> {line.text}</p>
              ))}
            </section>
          )}

          {report.truncated > 0 && (
            <p className="oqc-note">{reportLang === 'de'
              ? `${report.truncated} Mails waren länger als der Leserahmen und wurden am Ende gekürzt.`
              : `${report.truncated} emails ran past the reading window and were cut at the end.`}</p>
          )}
          {failedLanes.length > 0 && (
            <p className="oqc-note">
              {reportLang === 'de' ? 'Ausgefallen' : 'Unavailable'}: {failedLanes.map((lane) => LANE_LABELS[lane.name] || (String(lane.name).startsWith('detail') ? (reportLang === 'de' ? 'Text' : 'Writing') : lane.name)).join(', ')}.
              {reportLang === 'de' ? 'Die übrigen Spuren sind im Bericht.' : 'The other passes are in the report.'}
            </p>
          )}

          {(report.dimensions || []).reduce((acc, dim) => {
            const items = (report.findings || []).filter((item) => dimensionIdOf(item) === dim.id);
            if (!items.length) return acc;
            const startAt = acc.cursor;
            acc.cursor += items.length;
            acc.nodes.push(
              <FindingGroup
                key={dim.id}
                title={dimensionTitle(dim, reportLang)}
                items={items}
                sampleSize={report.sampleSize}
                mails={report.mails}
                startAt={startAt}
                lang={reportLang}
                marked={marked}
                onToggle={toggleMark}
              />
            );
            return acc;
          }, { cursor: findingStart, nodes: [] }).nodes}

          {fullRead.length > 0 && (
            <details className="oqc-more">
              <summary>{reportLang === 'de' ? 'Ganze Einordnung' : 'Full read'}</summary>
              {report.arrival && <p>{report.arrival}</p>}
              {report.repetition && <p>{report.repetition}</p>}
              {report.trust && <p>{report.trust}</p>}
            </details>
          )}
          {report.findings && report.findings.length === 0 && (
            <p className="oqc-empty">{reportLang === 'de' ? 'In dieser Stichprobe ist nichts hängen geblieben.' : 'Nothing stood out in this sample.'}</p>
          )}
        </div>
      )}
    </section>
  );
}

export default OutreachQualityCheck;
